import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Button, Toast, Dialog, Skeleton, NoticeBar } from 'react-vant';
import { ArrowLeft, LocationO, ManagerO, ClockO, UserCircleO } from '@react-vant/icons';
import { getSessionDetail, registerForClub, getServerTime } from '../../api/club';
import { getMyStudents } from '../../api/school';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

const ClubRegister = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector(state => state.user);
  const basePath = userInfo?.role === 'parent' || userInfo?.role === 'student' ? '/student/clubs' : '/clubs';
  
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeStatus, setTimeStatus] = useState('upcoming');
  const [timeLeftStr, setTimeLeftStr] = useState('');
  const [serverTimeOffset, setServerTimeOffset] = useState(0);

  useEffect(() => {
    fetchData();
  }, [sessionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionRes, studentsRes, timeRes] = await Promise.all([
        getSessionDetail(sessionId),
        getMyStudents(),
        getServerTime().catch(() => null)
      ]);
      
      let offset = 0;
      if (timeRes && timeRes.timestamp_ms) {
        offset = timeRes.timestamp_ms - Date.now();
        setServerTimeOffset(offset);
      }

      setSession(sessionRes);
      setStudents(studentsRes.data || []);
      
      if (studentsRes.data && studentsRes.data.length > 0) {
        setSelectedStudentId(studentsRes.data[0].id);
      }
      
      updateTimeStatus(sessionRes, offset);
    } catch (error) {
      Toast.fail('获取详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    const timer = setInterval(() => {
      updateTimeStatus(session, serverTimeOffset);
    }, 1000);
    return () => clearInterval(timer);
  }, [session, serverTimeOffset]);

  const updateTimeStatus = (sessionData, offset = serverTimeOffset) => {
    if (sessionData.status === 'ongoing') {
      setTimeStatus('ongoing');
      setTimeLeftStr('活动中');
      return;
    }
    if (sessionData.status === 'closed') {
      setTimeStatus('closed');
      setTimeLeftStr('已结束');
      return;
    }
    // 使用校准后的服务器时间基准
    const now = dayjs(Date.now() + offset);
    const start = dayjs(sessionData.registration_start);
    const end = dayjs(sessionData.registration_end);
    
    if (now.isBefore(start)) {
      setTimeStatus('upcoming');
      const diff = start.diff(now, 'second');
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = num => num.toString().padStart(2, '0');
      setTimeLeftStr(`${pad(h)}:${pad(m)}:${pad(s)}`);
    } else if (now.isAfter(end)) {
      setTimeStatus('closed');
      setTimeLeftStr('报名已结束');
    } else {
      setTimeStatus('open');
      setTimeLeftStr('抢报中');
    }
  };

  const handleRegister = async () => {
    if (!selectedStudentId) {
      Toast.info('请选择要报名的学生');
      return;
    }

    const selectedStudent = students.find(s => s.id === selectedStudentId);
    const myGradeStat = session?.grade_stats?.find(gs => gs.grade === selectedStudent?.grade);
    const isGradeFull = myGradeStat && myGradeStat.remaining <= 0;
    const isWaitlist = (session.current_count >= session.max_capacity) || isGradeFull;
    
    let message = '确定要为该学生报名此社团吗？';
    if (session.current_count >= session.max_capacity) {
      message = '社团总名额已满，继续报名将进入候补名单。是否继续？';
    } else if (isGradeFull) {
      message = `当前社团【${selectedStudent?.grade}】名额已满（限额 ${myGradeStat.quota} 席），继续报名将进入候补名单。是否继续？`;
    }

    Dialog.confirm({
      title: '报名确认',
      message,
      onConfirm: async () => {
        try {
          setSubmitting(true);
          const res = await registerForClub({
            session_id: parseInt(sessionId),
            student_id: selectedStudentId
          });
          
          if (res.status === 'confirmed') {
            Toast.success('报名成功！');
          } else if (res.status === 'waitlisted') {
            Toast.success('已加入候补名单！');
          }
          
          setTimeout(() => {
            navigate(`${basePath}/my-registrations`, { replace: true });
          }, 1500);
          
        } catch (error) {
          Toast.fail(error.response?.data?.detail || '报名失败，请重试');
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const renderRegisterButton = () => {
    if (timeStatus === 'upcoming') {
      return (
        <Button block disabled color="linear-gradient(to right, #e2e2e2, #cbd5e1)" style={{ fontSize: '18px', fontWeight: 'bold', height: 50, borderRadius: 25, color: '#fff', border: 'none' }}>
          距离开始还有: {timeLeftStr}
        </Button>
      );
    }
    
    if (timeStatus === 'ongoing') {
      return (
        <Button block disabled color="#f1f5f9" style={{ fontSize: '18px', fontWeight: 'bold', height: 50, borderRadius: 25, color: '#cbd5e1', border: 'none' }}>
          社团已在活动中
        </Button>
      );
    }
    
    if (timeStatus === 'closed') {
      return (
        <Button block disabled color="#f1f5f9" style={{ fontSize: '18px', fontWeight: 'bold', height: 50, borderRadius: 25, color: '#cbd5e1', border: 'none' }}>
          报名已结束
        </Button>
      );
    }
    
    const selectedStudent = students.find(s => s.id === selectedStudentId);
    const myGradeStat = session?.grade_stats?.find(gs => gs.grade === selectedStudent?.grade);
    const isGradeFull = myGradeStat && myGradeStat.remaining <= 0;
    const isFull = (session.current_count >= session.max_capacity) || isGradeFull;
    
    return (
      <Button 
        block 
        color={isFull ? "linear-gradient(to right, #ff5e1e, #e11d48)" : "linear-gradient(to right, #056b4f, #00b578)"} 
        loading={submitting}
        onClick={handleRegister}
        style={{ fontSize: '18px', fontWeight: '800', height: 50, borderRadius: 25, boxShadow: isFull ? '0 8px 16px rgba(255, 77, 79, 0.2)' : '0 8px 16px rgba(5, 107, 79, 0.2)', border: 'none' }}
      >
        {isFull ? '名额已满 (排队候补)' : '立即抢报'}
      </Button>
    );
  };

  if (loading || !session) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100vh', paddingTop: 60 }}>
        <NavBar title={<span style={{ fontWeight: 800 }}>社团详情</span>} leftText={<ArrowLeft size={20} />} onClickLeft={() => navigate(-1)} fixed border={false} />
        <div style={{ padding: '20px' }}><Skeleton title row={5} /></div>
      </div>
    );
  }

  const isFull = session.current_count >= session.max_capacity;
  const percentage = Math.min(100, Math.round((session.current_count / session.max_capacity) * 100));

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: '100px', fontFamily: '"Inter", "PingFang SC", "Microsoft YaHei", sans-serif' }}>
      <div style={{ 
          background: 'rgba(255, 255, 255, 0.85)', 
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          position: 'sticky', 
          top: 0, 
          zIndex: 100,
          borderBottom: '1px solid rgba(0,0,0,0.03)'
      }}>
          <NavBar 
            title={<span style={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>报名详情</span>} 
            leftText={<ArrowLeft size={20} color="#0f172a" />} 
            onClickLeft={() => navigate(-1)} 
            border={false}
            style={{ background: 'transparent' }}
          />
      </div>
      
      {((session.current_count >= session.max_capacity) || (session?.grade_stats?.find(gs => gs.grade === students.find(s => s.id === selectedStudentId)?.grade)?.remaining <= 0)) && timeStatus === 'open' && (
        <NoticeBar 
          leftIcon="info-o" 
          color="#ed6a0c" 
          background="#fffbe8" 
          text={
            session.current_count >= session.max_capacity
              ? "当前社团名额已满，继续报名将进入候补队列。若有人取消，候补成员将依次递补。"
              : `当前社团【${students.find(s => s.id === selectedStudentId)?.grade || ''}】配额已满，继续报名将进入候补队列。`
          } 
        />
      )}
      
      {/* 顶部社团卡片信息区 */}
      <div style={{ 
          margin: '16px', 
          padding: '24px', 
          background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', 
          borderRadius: '24px', 
          color: '#fff',
          boxShadow: '0 12px 24px rgba(5, 107, 79, 0.2)',
          position: 'relative',
          overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '60%', height: '100%', background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.15) 0%, transparent 70%)', filter: 'blur(20px)' }}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: 16, fontSize: 12, fontWeight: 700, marginBottom: 12, backdropFilter: 'blur(4px)' }}>
            {session.club?.category || '综合社团'}
          </div>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '26px', fontWeight: 800, lineHeight: 1.3 }}>{session.club?.name || session.club_name}</h2>
          
          <div style={{ display: 'grid', gap: '12px', fontSize: '14px', opacity: 0.9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ManagerO size={18} /> {session.club?.instructor_name || '暂无指导老师'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><LocationO size={18} /> {session.club?.venue || '待定'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ClockO size={18} /> {session.club?.schedule_info || '待定'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><UserCircleO size={18} /> 招生对象: {session.club?.grade_range?.join(', ') || '全体'}</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px' }}>
        {/* 社团介绍 */}
        {session.club?.description && (
          <div style={{ background: '#fff', borderRadius: '20px', padding: '20px', marginBottom: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }}></div>社团介绍
            </h3>
            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
              {session.club.description}
            </div>
          </div>
        )}
        
        {/* 报名进度 */}
        <div style={{ background: '#fff', borderRadius: '20px', padding: '20px', marginBottom: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }}></div>报名进度
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#475569' }}>已报 <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{session.current_count}</span> / {session.max_capacity}</div>
            <div style={{ fontSize: 13, color: '#d97706', fontWeight: 600, background: '#fff5f0', padding: '4px 10px', borderRadius: 12 }}>候补排队: {session.waitlist_count} 人</div>
          </div>
          <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${percentage}%`, background: isFull ? 'linear-gradient(90deg, #ff5e1e, #e11d48)' : 'linear-gradient(90deg, #00b578, #34d399)', borderRadius: 5, transition: 'width 0.5s ease-in-out' }}></div>
          </div>

          {session.grade_stats && session.grade_stats.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #f1f5f9' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>分年级名额配额</span>
                <span style={{ fontSize: 12, color: '#056b4f', fontWeight: 600 }}>年级独立限额</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {session.grade_stats.map(gs => {
                  const curStudent = students.find(s => s.id === selectedStudentId);
                  const isCurStudentGrade = curStudent?.grade === gs.grade;
                  const isGFull = gs.remaining <= 0;
                  return (
                    <div 
                      key={gs.grade} 
                      style={{ 
                        background: isCurStudentGrade ? '#f0fdf4' : '#f8fafc',
                        border: isCurStudentGrade ? '1.5px solid #056b4f' : '1px solid #f1f5f9',
                        padding: '6px 10px',
                        borderRadius: 10,
                        fontSize: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ fontWeight: isCurStudentGrade ? 800 : 500, color: isCurStudentGrade ? '#056b4f' : '#334155' }}>
                        {gs.grade} {isCurStudentGrade && '★'}
                      </span>
                      <span style={{ color: isGFull ? '#e11d48' : '#00b578', fontWeight: 700 }}>
                        {isGFull ? '已满额' : `余 ${gs.remaining} 席`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* 选择学生 */}
        <div style={{ background: '#fff', borderRadius: '20px', padding: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }}></div>选择报名学生
          </h3>
          {students.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0', fontSize: 14 }}>您还没有绑定学生信息，请先前往主页进行绑定</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {students.map(student => {
                const isSelected = selectedStudentId === student.id;
                return (
                  <div 
                    key={student.id}
                    onClick={() => setSelectedStudentId(student.id)}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '16px',
                      border: `2px solid ${isSelected ? '#056b4f' : '#f1f5f9'}`,
                      background: isSelected ? '#f0fdf4' : '#fff',
                      color: isSelected ? '#056b4f' : '#0f172a',
                      fontSize: '16px',
                      fontWeight: isSelected ? 800 : 500,
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? '0 4px 12px rgba(5, 107, 79, 0.1)' : 'none'
                    }}
                  >
                    {student.real_name}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ 
        position: 'fixed', 
        bottom: 0, left: 0, right: 0, 
        padding: '16px 20px', 
        background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.03)',
        borderTop: '1px solid rgba(0,0,0,0.05)',
        zIndex: 100
      }}>
        {renderRegisterButton()}
      </div>
    </div>
  );
};

export default ClubRegister;
